import { IWindowService, IHttpService, jwt, IPromise, IRootScopeService, IHttpResponse, IQService } from "angular";
import { Ng1StateDeclaration } from "@uirouter/angularjs";
import { IModalStackService } from "angular-ui-bootstrap";
import { storage } from "@gtmhub/core/storage";
import { IAuthResponse, buildAuth0LoginUrl, IAuthMultipleDomainsResponse, getAuth0 } from "@gtmhub/auth";
import { IAppConfig } from "@gtmhub/env";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { CurrentUserService } from "../users";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { IProfile } from "@webapp/users/models/users.models";
import { removeWebsiteEmailCookie } from "@gtmhub/website";

/**
 * Gets Auth0 user ID from localStorage. Auth0 user ID usually
 * has this form: "xxxx|dasd-a324resafdadv3-aavwarawvw"
 */
export const getAuth0UserId = (): string => storage.get("auth0UserId");

/**
 * Sets Auth0 user ID to localStorage
 * @param userId the Auth0 user ID
 */
export const setAuth0UserId = (userId: string) => storage.set("auth0UserId", userId);

interface IGtmhubJwtToken extends jwt.JwtToken {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  given_name?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  last_name?: string;
  name?: string;
  username?: string;
  nickname?: string;
  email?: string;
}

const getIdToken = (): string => JSON.parse(localStorage.getItem("token"));

const givenName = (token: IGtmhubJwtToken): string => [token.given_name, token.last_name].filter((x) => x != null).join(" ");

export const refreshTokenSilently = async () => {
  if ("auth0" in localStorage) {
    const auth0 = getAuth0();
    const authenticated = await auth0.isAuthenticated();
    if (authenticated) {
      const token = await auth0.getTokenSilently();
      localStorage.setItem("accessToken", token);
    }
  }
};

export const getToken = (): string => localStorage.getItem("accessToken") || getIdToken();

export class AuthenticationResolverService {
  public static $inject = [
    "$rootScope",
    "$window",
    "$uibModalStack",
    "$http",
    "$q",
    "jwtHelper",
    "appConfig",
    "AnalyticsService",
    "CurrentUserService",
    "FeatureToggleService",
    "AccountResolverService",
  ];

  constructor(
    private $rootScope: IRootScopeService,
    private $window: IWindowService,
    private $uibModalStack: IModalStackService,
    private $http: IHttpService,
    private $q: IQService,
    private jwtHelper: jwt.IJwtHelper,
    private appConfig: IAppConfig,
    private analyticsService: AnalyticsService,
    private currentUserService: CurrentUserService,
    private featureToggleService: FeatureToggleService,
    private accountResolverService: AccountResolverService
  ) {}

  public getUserDisplayNameFromIdToken(): string {
    const idToken = getIdToken();
    if (!idToken) {
      return null;
    }

    try {
      const token: IGtmhubJwtToken = this.jwtHelper.decodeToken(idToken);
      return token && (token.name || givenName(token) || token.username || token.nickname || token.email);
    } catch (err) {
      return null;
    }
  }

  // todo: this must be refactored since the current state is awful. This service does not depend on any of the services that are owners
  // of the store values but at the same time it clears them to ensure that no stale data is left out when you logout.
  // a possible solution is to have some dependency registration mechanism - all services that need to clean data on logout should register
  // this way the authenticationResolverService is not bogged down with billion dependencies as it is now
  public clear(): void {
    localStorage.removeItem("auth0UserId");
    localStorage.removeItem("auth0");
    localStorage.removeItem("token");
    localStorage.removeItem("accessToken");
    storage.remove("collapsedEntities");

    this.clearCookiesOnLogout();
  }

  private clearCookiesOnLogout(): void {
    this.currentUserService.removeMarketplaceRedirectCookie();
    removeWebsiteEmailCookie(document.location.host);
  }

  public navigateToLogin(user?: IProfile, { isResolved }: { isResolved?: boolean } = { isResolved: true }): IPromise<void> {
    const { host } = window.location;
    const email = user ? user.email : undefined;

    if (!email && host === this.appConfig.endpoints.webappEndpoint) {
      return this.$q.resolve().then(() => this.redirect(this.webappLoginUrl()));
    }

    return this.isSsoLogoutEnabled({ hasState: isResolved })
      .then((ssoEnabled) => this.resolveRedirectUrl({ email, ssoEnabled }))
      .then((url) => this.redirect(url));
  }

  private resolveRedirectUrl(args: { email?: string; ssoEnabled: boolean }): IPromise<string> {
    const currentGtmhubDomain = this.currentDomain();

    const makeRequest = (data: { email?: string; domain?: string }) =>
      this.$http.post<IAuthResponse>("/auth", data).then(
        (response) => {
          return this.resolveUrl(response, { email: args.email, currentGtmhubDomain: currentGtmhubDomain, ssoEnabled: args.ssoEnabled });
        },
        (response: IHttpResponse<unknown>) => {
          const userHasMultipleDomains = response.status === 409;
          if (userHasMultipleDomains) {
            // Try to resolve the current Gtmhub domain from the list of domains
            // and if it exists there, make a second request to get the proper details
            // for the redirect URL
            const multipleDomainsResponse = response as IHttpResponse<IAuthMultipleDomainsResponse>;
            const isCurrentGtmhubDomainInList = multipleDomainsResponse.data.domains.some((domain) => domain === currentGtmhubDomain);
            if (isCurrentGtmhubDomainInList) {
              return makeRequest({ ...data, domain: currentGtmhubDomain });
            }

            // just redirect to the current domain,
            // the login page will fix it if necessary
            return `${currentGtmhubDomain}/login?email=${args.email}`;
          } else {
            // in case of any error, we go to the general login page
            return this.webappLoginUrl(args.email);
          }
        }
      );

    return makeRequest(args.email ? { email: args.email } : { domain: currentGtmhubDomain });
  }

  private stateRequiresLogin(state: Ng1StateDeclaration): boolean {
    return state && state.data && state.data.requiresLogin;
  }

  private isAuthenticationDataPresentAndValid(): IPromise<boolean> {
    if ("auth0" in localStorage) {
      const auth0 = getAuth0();
      if (auth0) {
        return this.$q.when(auth0.isAuthenticated());
      }
      return this.$q.resolve(false);
    }
    const token = getToken();
    const auth0UserId = getAuth0UserId();

    const isAuthenticationDataValid = !!token && !this.jwtHelper.isTokenExpired(token);
    const isAuthenticationDataPresent = !!token && !!auth0UserId;

    return this.$q.resolve(isAuthenticationDataPresent && isAuthenticationDataValid);
  }

  public demandsAreMet(toState: Ng1StateDeclaration): IPromise<boolean> {
    if (!this.stateRequiresLogin(toState)) {
      return this.$q.resolve(true);
    }
    return this.isAuthenticationDataPresentAndValid();
  }

  public signout(): void {
    this.clear();
    this.raiseUserSignedOut();
  }

  public logout(meta: { ssoLogoutIfEnabled: boolean }): void {
    this.analyticsService.track("Signed Out");
    this.analyticsService.reset();

    let presentAccountState = true;
    const account = this.accountResolverService.getAccountData();
    if (!account) {
      presentAccountState = false;
    }

    this.isSsoLogoutEnabled({ hasState: meta.ssoLogoutIfEnabled && presentAccountState }).then((fflagEnabled) => {
      const isFederated = meta.ssoLogoutIfEnabled && fflagEnabled;
      const pkceFlowActive = "auth0" in localStorage;

      this.signout();

      if (pkceFlowActive) {
        return this.$q.when(getAuth0().logout({ federated: isFederated, returnTo: `${this.currentDomain()}/logout-success` }));
      } else {
        const logoutUrl = isFederated ? `${this.currentDomain()}/logout?federated` : `${this.currentDomain()}/logout`;
        this.redirect(logoutUrl);
      }
    });
  }

  private currentDomain(): string {
    const { protocol, host } = window.location;
    return `${protocol}//${host}`;
  }

  private isSsoLogoutEnabled({ hasState }: { hasState: boolean }): IPromise<boolean> {
    return hasState ? this.featureToggleService.isFeatureAvailable("automatic-sso-provider-logout") : this.$q.resolve(false);
  }

  private resolveUrl(response: IHttpResponse<IAuthResponse>, options: { email: string; currentGtmhubDomain: string; ssoEnabled: boolean }) {
    const auth = response.data;

    // if coming from ms-teams - the cookie should contain "SameSite=None" due to chrome security concern
    const isMsTeamsState = this.$window.location.href.includes("msteams");

    if (options.ssoEnabled) {
      return this.buildAuth0ssoLogoutAndLoginUrl(auth, options.currentGtmhubDomain);
    }

    if (auth.connections && auth.connections.length === 1) {
      return this.$q.when(
        buildAuth0LoginUrl(auth, options.email, {
          appState: { returnPath: response.data.returnPath },
          isMsTeamsState,
          bypassDomainCheck: this.appConfig.env.bypassDomainCheck,
          connection: auth.connections[0],
        })
      );
    } else if (auth.connections && auth.connections.length > 1) {
      return `${options.currentGtmhubDomain}/login?domain=${options.currentGtmhubDomain}`;
    } else {
      return this.$q.when(
        buildAuth0LoginUrl(auth, options.email, {
          appState: { returnPath: response.data.returnPath },
          isMsTeamsState,
          bypassDomainCheck: this.appConfig.env.bypassDomainCheck,
        })
      );
    }
  }

  public logoutToWebappLogoutUrl(): void {
    this.analyticsService.track("Signed Out");
    this.analyticsService.reset();

    this.$uibModalStack.dismissAll();
    this.signout();

    this.$window.location.href = this.webappLogoutUrl();
  }

  private raiseUserSignedOut() {
    this.$rootScope.$broadcast("userSignedOut");
  }

  private redirect(url: string): void {
    this.$uibModalStack.dismissAll();
    this.$window.location.href = url;
  }

  private webappLoginUrl(email?: string): string {
    const { protocol } = location;
    const { webappEndpoint } = this.appConfig.endpoints;
    const query = email ? `?email=${email}` : "";
    return `${protocol}//${webappEndpoint}/login${query}`;
  }

  private webappLogoutUrl(): string {
    const { protocol } = location;
    const { webappEndpoint } = this.appConfig.endpoints;
    return `${protocol}//${webappEndpoint}/logout`;
  }

  private buildAuth0ssoLogoutAndLoginUrl(auth: IAuthResponse, currentGtmhubDomain: string): string {
    const baseUrl = `https://${auth.authDomain}/v2/logout`;
    const clientId = auth.clientId;
    const returnTo = `${currentGtmhubDomain}/login`;
    return `${baseUrl}?federated&clientId=${clientId}&returnTo=${returnTo}`;
  }
}
